import { useState } from 'react';
import type { CameraFeature } from '@tsr/shared-types';
import { LiveCameraPlayer, shouldProxyStream } from './LiveCameraPlayer';
import { MjpegPlayer } from './MjpegPlayer';
import { FreshnessIndicator } from './FreshnessIndicator';

/**
 * The media block for a camera: a live player when the camera is streaming,
 * otherwise the latest snapshot with a manual refresh. Extracted from the side
 * panel (Phase V) so the same rendering is reused by the click-to-fly overlay
 * and the multi-monitor pop-out window.
 *
 * `variant='wall'` (#232) renders the same media DECISIONS (which player /
 * which frame — the #256 axes) as a full-bleed, chrome-free mosaic tile: media
 * fills + centre-crops, and the panel's banners / freshness timeline / refresh
 * button / LIVE badges are dropped — the wall tile's caption carries the
 * honest state + freshness instead (`wallMediaLabel`), so the two surfaces
 * can never disagree about what the operator is looking at.
 */
export function CameraMedia({
  camera,
  enablePopout = true,
  variant = 'panel',
  onFreshFrame,
  onPlaybackState,
}: {
  camera: CameraFeature;
  enablePopout?: boolean;
  variant?: 'panel' | 'wall';
  /** Wall variant only: fired when a snapshot load counts as a genuinely-fresh
   *  proxied frame (the QA-3 (c) rule — never for broken / resolver-stale
   *  sources), so the wall caption can show the honest live age. */
  onFreshFrame?: () => void;
  /** Wall variant only: the mounted player's honest runtime, reported up so the
   *  tile caption can never claim LIVE over a body showing a down stream. */
  onPlaybackState?: (state: 'loading' | 'playing' | 'no_video' | 'offline') => void;
}): JSX.Element {
  const wall = variant === 'wall';
  const [bust, setBust] = useState(0);
  // QA-3 (c): wall-clock of the last successful snapshot load this view. The
  // proxy serves a live frame every view but only stamps last_snapshot_at on the
  // server, so the cached detail can read days stale; this lets FreshnessIndicator
  // show the honest live age instead. Only set for genuinely-fresh frames (see
  // freshFromProxy below) — broken/stale sources keep their real DB timestamp.
  const [proxiedAtMs, setProxiedAtMs] = useState<number | null>(null);
  const src = `/api/cameras/${camera.id}/snapshot${bust > 0 ? `?t=${bust}` : ''}`;
  const streamUrl = camera.properties.stream_url;
  const status = camera.properties.status;
  // Gate for the live video player: a real stream we can mount (HLS/youtube/etc).
  const isLive = status === 'live' && !!streamUrl;

  // youtube_live resolves its CURRENT live videoId at VIEW-TIME inside
  // YouTubeLivePlayer (via /api/cameras/:id/youtube — a direct pin, a
  // channel-resolve, or the camstreamer two-hop), so a stale or `degraded`
  // catalog `status` must NEVER bounce it off-platform: YouTube is embeddable and
  // we always play it in-app (founder posture 2026-06-05). It's playable whenever
  // the row is youtube_live, even with a NULL stream_url — the player resolves
  // from metadata, not the (often clobbered) stream_url. The worst case is the
  // player's own HONEST "Stream not live right now" / "unavailable", never the
  // off-platform FeedUnavailableNotice. Mirrors the view_time_resolver exemption
  // below (liveness lives at view-time, not in the cached catalog row). (#363)
  const isYouTubeLive = camera.properties.stream_type === 'youtube_live';

  // View-time resolvers (ALERTWest, Roundshot — Q.2.4/2.5) carry only a
  // snapshot_url and re-resolve a CURRENT frame on each fetch, so they never
  // have a stream_url. Their freshness lives in `status`: live = the resolver
  // returned a fresh frame, degraded = the source stopped updating. We DON'T
  // mount the live player (there's no stream), but a fresh frame is genuinely
  // live, so we badge it and suppress the false "Source broken" banner that the
  // OTHER/redirect url_health verdict would otherwise raise (Q.3.1).
  const isViewTimeResolver = camera.properties.view_time_resolver === true;
  const viewTimeFresh = isViewTimeResolver && status === 'live';
  const viewTimeStale = isViewTimeResolver && status !== 'live';
  // Source-level refresh cadence, lifted onto the list feature as a top-level
  // property (Phase V.1). Falls back to metadata for older/detail payloads.
  // Drives both the mp4_clip player's re-pull interval and the snapshot
  // freshness timeline below.
  const metadata = (camera.properties as { metadata?: Record<string, unknown> }).metadata;
  const rawRefresh = camera.properties.refresh_interval_s ?? metadata?.refresh_interval_s;
  const refreshIntervalS = typeof rawRefresh === 'number' ? rawRefresh : undefined;

  // #256 founder ruling: QC 511 / MTQ looping-MP4 clip cams are CLASSIFIED
  // stream_type='snapshot' (an on-demand clip is not live video — no live
  // family, no country-zoom tier), but their MEDIA is still the clip.
  // media_format='mp4_clip' (lifted top-level; metadata fallback for older
  // detail payloads) routes playback to the mp4_clip strategy, so the player
  // experience — looping clip + cold-start patience copy — is unchanged.
  const mediaFormat = camera.properties.media_format ?? metadata?.media_format;
  const playbackStreamType =
    mediaFormat === 'mp4_clip' ? ('mp4_clip' as const) : camera.properties.stream_type;

  // Q.1 §2.2: a probed snapshot URL that isn't HEALTHY gets a "broken since…"
  // banner ABOVE the (still-shown) image, so the operator sees the source is
  // degraded without it being hidden. View-time resolvers are EXCLUDED here —
  // their indirection probes as OTHER/redirect even when fresh, so url_health
  // is the wrong signal for them; they use the resolver-stale banner below.
  const health = camera.properties.url_health;
  const isUnhealthy =
    !isLive && !isViewTimeResolver && !!health && health.status !== 'HEALTHY';

  // A degraded row with no playable media at all — no live stream, no snapshot
  // URL to fetch (#123 Verkada: the embed page can't be ingested natively). We
  // refuse to mount a broken <img> over nothing; instead we show an honest
  // "feed not available here" notice with a click-through to the operator's own
  // viewer page. Keyed on status='degraded' (not snapshot_url alone) so ordinary
  // snapshot cameras — whose fixtures may omit snapshot_url — are unaffected.
  const snapshotUrl = (camera.properties as { snapshot_url?: string | null }).snapshot_url ?? null;
  // youtube_live is EXEMPT (see isYouTubeLive above): it's always played in-app via
  // the view-time resolver, so it must never fall into the off-platform notice —
  // even when the catalog row is degraded with a clobbered (NULL) stream_url. Only
  // genuinely un-embeddable rows (Verkada #123 / NSWebcams #360: no stream, no
  // snapshot, not youtube) still surface FeedUnavailableNotice.
  const noNativeMedia =
    !isLive && !isYouTubeLive && !isViewTimeResolver && status === 'degraded' && !snapshotUrl;

  // Native Verkada playback (#123): the feed is mirrored in-app for evaluation
  // pending a partnership (founder decision 2026-06-05). The marker is stamped
  // on the row by the verkada-native-playback resolver script; surface it as an
  // honest note under the live player so the operator knows the access posture.
  const mirroredPendingPermission =
    !!metadata && 'verkada_native_playback' in metadata;

  if (noNativeMedia) {
    if (wall) {
      // Wall tile: the honest no-media state, sized to the tile, no card chrome.
      return (
        <div
          data-testid="feed-unavailable-notice"
          className="flex h-full w-full items-center justify-center bg-black px-4 text-center text-xs text-slate-500"
        >
          Live feed not available here
        </div>
      );
    }
    return <FeedUnavailableNotice camera={camera} />;
  }

  if (isLive || isYouTubeLive) {
    // MJPEG (Axis video.cgi: Tomahawk + Trail BC, and native-MJPEG Ozolio cams)
    // renders as a multipart <img>, not <video>/HLS — a wholly different player.
    // Always proxied same-origin. (HLS-format Ozolio cams keep stream_type='hls'
    // and fall through to the LiveCameraPlayer below, which proxies the embed via
    // shouldProxyStream → /stream 302s to the CORS-open playlist.)
    if (camera.properties.stream_type === 'mjpeg') {
      return (
        <div className={wall ? 'h-full w-full' : 'space-y-2'}>
          <MjpegPlayer
            cameraId={camera.id}
            name={camera.properties.name}
            enablePopout={enablePopout && !wall}
            fill={wall}
            onPlaybackState={wall ? onPlaybackState : undefined}
          />
          {mirroredPendingPermission && !wall && <MirroredFeedNote />}
        </div>
      );
    }
    // Live video replaces the snapshot still + refresh button (those are only
    // meaningful for snapshot-based cameras).
    return (
      <div className={wall ? 'h-full w-full' : 'space-y-2'}>
        <LiveCameraPlayer
          cameraId={camera.id}
          // youtube_live ignores streamUrl entirely (it resolves the current
          // videoId from /api/cameras/:id/youtube), so '' is a safe placeholder
          // for a clobbered NULL stream_url. Non-youtube live rows always carry a
          // real stream_url (isLive requires it), so this is a no-op for them.
          streamUrl={streamUrl ?? ''}
          useProxy={!!streamUrl && shouldProxyStream(streamUrl)}
          name={camera.properties.name}
          source={camera.properties.source}
          streamType={playbackStreamType}
          refreshIntervalS={refreshIntervalS}
          enablePopout={enablePopout && !wall}
          fill={wall}
          onPlaybackState={wall ? onPlaybackState : undefined}
        />
        {mirroredPendingPermission && !wall && <MirroredFeedNote />}
      </div>
    );
  }

  if (wall) {
    // Wall snapshot tile (#232): the bare full-bleed frame, centre-cropped to
    // the tile. No freshness timeline / banners / refresh button / view-time
    // LIVE badge here — the tile caption derives the honest SNAPSHOT/CLIP state
    // + age from the SAME properties (`wallMediaLabel`), so chrome the wall
    // can't host never silently hides a contradiction.
    return (
      <img
        data-testid="snapshot"
        src={src}
        // Same freshness rule as the panel's proxiedAtMs (QA-3 (c)): a load is
        // only a "fresh frame" when the source isn't broken or resolver-stale.
        onLoad={() => {
          if (!isUnhealthy && !viewTimeStale) onFreshFrame?.();
        }}
        alt={`Snapshot from ${camera.properties.name}`}
        className="block h-full w-full object-cover"
      />
    );
  }

  return (
    <>
      {isUnhealthy && health && (
        <BrokenSourceBanner
          status={health.status}
          firstFailedAt={health.first_failed_at ?? health.checked_at}
          contentUnavailable={!!health.content_unavailable}
        />
      )}
      {viewTimeStale && (
        // A view-time resolver whose last resolve didn't return a fresh frame
        // (degraded). The source stopped updating — banner the staleness with
        // resolver context rather than the (irrelevant) url_health verdict.
        <ResolverStaleBanner lastSnapshotAt={camera.properties.last_snapshot_at} />
      )}
      <FreshnessIndicator
        capturedAt={camera.properties.captured_at ?? null}
        lastSnapshotAt={camera.properties.last_snapshot_at}
        proxiedAtMs={proxiedAtMs}
        refreshIntervalS={refreshIntervalS}
      />
      <div className="relative rounded overflow-hidden border border-slate-700 bg-slate-950">
        <img
          key={bust}
          data-testid="snapshot"
          src={src}
          // QA-3 (c): a successful load means the proxy just served a fresh live
          // frame. Don't trust the load for broken (url_health) or stale view-time
          // resolver sources — those can serve a last-good frame, so let their real
          // (honestly stale) DB timestamp show instead.
          onLoad={() => {
            if (!isUnhealthy && !viewTimeStale) setProxiedAtMs(Date.now());
          }}
          alt={`Snapshot from ${camera.properties.name}`}
          /* Stage 5 §5.3: cap tall panorama / portrait snapshots so the image
           * can't push the back button below the fold. 60vh minus ~80px leaves
           * room for the banner above and the back button below; object-contain
           * preserves aspect ratio, and shorter/square images stay at their
           * natural size up to this ceiling (max-height, not height). */
          className="block w-full max-h-[calc(60vh-80px)] object-contain"
        />
        {viewTimeFresh && (
          // Fresh view-time-resolver frame: no <video> stream to host the
          // LiveCameraPlayer's badge, so we overlay an equivalent LIVE badge on
          // the snapshot itself (Q.3.1). Mirrors LiveCameraPlayer's styling.
          <div
            data-testid="view-time-live-badge"
            className="absolute left-2 top-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-400"
          >
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
            LIVE
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setBust(Date.now())}
        className="w-full text-sm py-1.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700"
      >
        Refresh snapshot
      </button>
    </>
  );
}

/** Honest degraded state for a row whose feed isn't natively ingestible (#123
 *  Verkada: token-gated commercial HLS we won't speculatively proxy). No live
 *  badge, no broken image — a clear notice plus a click-through to the operator's
 *  own viewer page when we have one. Mirrors the EarthCam / CN-Tower precedent. */
function FeedUnavailableNotice({ camera }: { camera: CameraFeature }): JSX.Element {
  const viewerUrl = camera.properties.viewer_url;
  return (
    <div
      data-testid="feed-unavailable-notice"
      className="flex flex-col items-center gap-2 rounded border border-slate-700 bg-slate-900/60 px-4 py-6 text-center"
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7 text-slate-500" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2" y="6" width="14" height="12" rx="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m16 10 6-3v10l-6-3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m3 3 18 18" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-sm font-medium text-slate-200">Live feed not available here</span>
      <span className="max-w-xs text-xs text-slate-400">
        This camera streams through a token-gated platform with no native ingest
        path, so we don&apos;t mirror it in-app. The operator&apos;s own page still
        shows it live.
      </span>
      {viewerUrl && (
        <a
          href={viewerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="feed-unavailable-viewer-link"
          className="mt-1 inline-block rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-sky-300 hover:bg-slate-700"
        >
          Open live view ↗
        </a>
      )}
    </div>
  );
}

/** Honesty note under a mirrored Verkada live feed (#123): we play it in-app for
 *  evaluation, but access still requires a partnership. Mirrors the founder
 *  decision (2026-06-05) — surfaced wherever the live player renders (panel,
 *  overlay, pop-out). */
function MirroredFeedNote(): JSX.Element {
  return (
    <div
      data-testid="mirrored-feed-note"
      className="flex items-start gap-2 rounded border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs text-slate-400"
    >
      <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>
        Feed mirrored for evaluation pending permission — access to this camera
        requires a partnership.
      </span>
    </div>
  );
}

/** ISO timestamp → YYYY-MM-DD, or null when unparseable. */
function isoDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = iso.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

/** "Source broken since…" banner shown above the (still-rendered) broken image
 *  area for a degraded snapshot source (Q.1 §2.2). The image itself stays
 *  visible below — we annotate, we don't hide. */
function BrokenSourceBanner({
  status,
  firstFailedAt,
  contentUnavailable,
}: {
  status: string;
  firstFailedAt: string | null;
  contentUnavailable: boolean;
}): JSX.Element {
  const isPlaceholder = contentUnavailable || status === 'PLACEHOLDER';
  const when = isoDate(firstFailedAt);
  const headline = when ? `Source broken since ${when}` : 'Source is broken';
  const detail = isPlaceholder
    ? 'The upstream feed is returning a placeholder image, not a live frame.'
    : `The upstream image endpoint is failing (HTTP ${status}).`;
  return (
    <div
      data-testid="broken-source-banner"
      className="flex items-start gap-2 rounded border border-amber-700/60 bg-amber-950/40 px-3 py-2"
    >
      <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 9v4M12 17h.01" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-amber-200">{headline}</span>
        <span className="text-xs text-amber-200/70">{detail}</span>
      </div>
    </div>
  );
}

/** Banner for a view-time resolver (ALERTWest / Roundshot) whose last resolve
 *  didn't return a fresh frame — the source stopped updating (status=degraded).
 *  Distinct from BrokenSourceBanner: these rows are healthy endpoints serving a
 *  stale frame, so we frame it as resolver staleness, not a broken URL (Q.3.1). */
function ResolverStaleBanner({
  lastSnapshotAt,
}: {
  lastSnapshotAt: string | null;
}): JSX.Element {
  const when = isoDate(lastSnapshotAt);
  return (
    <div
      data-testid="resolver-stale-banner"
      className="flex items-start gap-2 rounded border border-amber-700/60 bg-amber-950/40 px-3 py-2"
    >
      <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-amber-200">Live feed stale</span>
        <span className="text-xs text-amber-200/70">
          This camera re-resolves a current frame on each view, but the source has
          stopped updating{when ? ` — last fresh frame ${when}` : ''}. The image
          shown may be old.
        </span>
      </div>
    </div>
  );
}
